import { parseOrgDepartment, type OrgStructureRow } from './org-structure';
import type {
  CapabilityAssignment,
  CapabilityDefinition,
  CapabilityMetrics,
} from './capabilities';

export interface OrgPosition {
  positionId: string;
  title: string;
  department?: OrgStructureRow['department'];
  reportsTo?: string | null;
  storeId?: string | null;
  isManager?: boolean;
  roleType?: string;
  personId?: string | null;
  baseCost?: number | null;
}

export interface OrgPerson {
  personId: string;
  name: string;
  positionId: string;
  employmentType?: string;
  fte?: number | null;
  cost?: number | null;
}

export interface FinancialMetrics {
  totalHeadcount: number;
  totalFTE: number;
  totalLaborCost: number;
  averageCostPerFTE: number | null;
}

export interface OrgState {
  orgStateId: string;
  tenantId: string;
  name: string;
  positions: OrgPosition[];
  people?: OrgPerson[];
  capabilityDefinitions?: CapabilityDefinition[];
  capabilityAssignments?: CapabilityAssignment[];
}

export interface OrgMetrics {
  maxLayersFromCeoToStoreAssociate: number | null;
  avgSpanStoreManager: number;
  districtManagersWithSpanTooHigh: string[];
  districtManagersWithSpanTooLow: string[];
  totalHeadcount: number;
  financial: FinancialMetrics;
  capabilityMetrics: CapabilityMetrics;
}

export type RuleSeverity = 'low' | 'medium' | 'high';

export interface RuleViolation {
  ruleId: string;
  title?: string;
  description?: string;
  severity?: RuleSeverity;
  details?: Record<string, unknown>;
}

export interface OrgXrayResponse {
  tenantId: string;
  strategyStateId: string;
  orgStateId: string;
  metrics: OrgMetrics;
  ruleViolations: RuleViolation[];
}

const buildError = (message: string) => new Error(message);

const isCapabilityDefinitions = (input: unknown): input is CapabilityDefinition[] => {
  if (!Array.isArray(input)) return false;
  return input.every(
    (item) =>
      item &&
      typeof item === 'object' &&
      typeof (item as CapabilityDefinition).id === 'string' &&
      typeof (item as CapabilityDefinition).tenantId === 'string' &&
      typeof (item as CapabilityDefinition).name === 'string' &&
      typeof (item as CapabilityDefinition).description === 'string' &&
      typeof (item as CapabilityDefinition).category === 'string',
  );
};

const isCapabilityAssignments = (input: unknown): input is CapabilityAssignment[] => {
  if (!Array.isArray(input)) return false;
  return input.every(
    (item) =>
      item &&
      typeof item === 'object' &&
      typeof (item as CapabilityAssignment).positionId === 'string' &&
      typeof (item as CapabilityAssignment).capabilityId === 'string' &&
      typeof (item as CapabilityAssignment).strength === 'string',
  );
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isOptionalString = (value: unknown): value is string | null | undefined =>
  value === undefined || value === null || typeof value === 'string';

export const OrgStateSchema = {
  parse(input: unknown): OrgState {
    if (typeof input !== 'object' || input === null) {
      throw buildError('OrgState must be an object');
    }
    const data = input as Record<string, any>;
    if (!isString(data.orgStateId) || !isString(data.tenantId) || !isString(data.name)) {
      throw buildError('orgStateId, tenantId, and name are required strings');
    }
    if (!Array.isArray(data.positions) || data.positions.length === 0) {
      throw buildError('positions must be a non-empty array');
    }
    const positions: OrgPosition[] = data.positions.map((pos: any) => {
      if (!isString(pos.positionId) || !isString(pos.title)) {
        throw buildError('Each position requires positionId and title');
      }
      if (!isOptionalString(pos.reportsTo)) {
        throw buildError('reportsTo must be a string when provided');
      }
      if (!isOptionalString(pos.storeId)) {
        throw buildError('storeId must be a string when provided');
      }
      if (!isOptionalString(pos.personId)) {
        throw buildError('personId must be a string when provided');
      }
      const department = parseOrgDepartment(pos.department);
      const baseCostValid = pos.baseCost === undefined || pos.baseCost === null || typeof pos.baseCost === 'number';
      if (!baseCostValid) {
        throw buildError('baseCost must be numeric when provided');
      }
      return {
        positionId: pos.positionId,
        title: pos.title,
        ...(department === undefined ? {} : { department }),
        reportsTo: pos.reportsTo ?? null,
        storeId: pos.storeId ?? null,
        isManager: pos.isManager ?? false,
        roleType: pos.roleType,
        personId: pos.personId ?? null,
        baseCost: pos.baseCost ?? null,
      };
    });

    const people: OrgPerson[] | undefined = Array.isArray(data.people)
      ? data.people.map((person: any) => {
          if (!isString(person.personId) || !isString(person.name) || !isString(person.positionId)) {
            throw buildError('Each person requires personId, name, and positionId');
          }
          const fteValid = person.fte === undefined || person.fte === null || typeof person.fte === 'number';
          if (!fteValid) {
            throw buildError('Person fte must be numeric when provided');
          }
          const costValid = person.cost === undefined || person.cost === null || typeof person.cost === 'number';
          if (!costValid) {
            throw buildError('Person cost must be numeric when provided');
          }
          return {
            personId: person.personId,
            name: person.name,
            positionId: person.positionId,
            employmentType: person.employmentType,
            fte: person.fte ?? null,
            cost: person.cost ?? null,
          };
        })
      : undefined;

    const capabilityDefinitions = isCapabilityDefinitions(data.capabilityDefinitions)
      ? data.capabilityDefinitions
      : undefined;

    const capabilityAssignments = isCapabilityAssignments(data.capabilityAssignments)
      ? data.capabilityAssignments
      : undefined;

    return {
      orgStateId: data.orgStateId,
      tenantId: data.tenantId,
      name: data.name,
      positions,
      people,
      capabilityDefinitions,
      capabilityAssignments,
    };
  },
};
